import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  usePharmacyCompanies,
  useCreatePharmacyCompany,
  useUpdatePharmacyCompany,
  useDeletePharmacyCompany,
  useBulkGeneratePharmacyCompanyCodes,
  useBulkDeletePharmacyCompanies,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { PharmacyLookupPage } from './PharmacyLookupPage';
import { Building2 } from 'lucide-react';
import { toast } from 'sonner';
import type { CreateLookupDto } from '../../services/httpServices/pharmacyDrugService';

/**
 * Generate a short company code from its name.
 * - Multiple words: initial of each word (Square Pharmaceuticals Ltd. -> SPL)
 * - Single word: first 3 letters (Incepta -> INC)
 */
function generateCompanyCode(name: string): string {
  const cleaned = name.replace(/[^a-zA-Z0-9\s]/g, ' ').trim();
  if (!cleaned) return '';
  const words = cleaned.split(/\s+/).filter(Boolean);
  const raw = words.length > 1 ? words.map((w) => w[0]).join('') : words[0];
  return raw.slice(0, 3).toUpperCase();
}

export function PharmacyCompanies() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const limit = 20;

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [formData, setFormData] = useState<CreateLookupDto>({ name: '', code: '', contact: '', description: '', isActive: true });

  const { data, isLoading, error } = usePharmacyCompanies(page, limit, debouncedSearch || undefined);
  const meta = data?.meta;
  const items = data?.data || [];

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const createMutation = useCreatePharmacyCompany();
  const updateMutation = useUpdatePharmacyCompany();
  const deleteMutation = useDeletePharmacyCompany();
  const bulkGenerateMutation = useBulkGeneratePharmacyCompanyCodes();
  const bulkDeleteMutation = useBulkDeletePharmacyCompanies();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItem) {
      await updateMutation.mutateAsync({ id: editingItem.id, data: formData });
    } else {
      await createMutation.mutateAsync(formData);
    }
    setModalOpen(false);
    setFormData({ name: '', code: '', contact: '', description: '', isActive: true });
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  const handleBulkGenerateCodes = async (selected: any[]) => {
    const payload = selected
      .filter((item) => !item.code && generateCompanyCode(item.name || ''))
      .map((item) => ({ id: item.id, code: generateCompanyCode(item.name) }));
    if (payload.length === 0) {
      toast.info('All selected companies already have a code');
      return;
    }
    try {
      await bulkGenerateMutation.mutateAsync(payload);
      toast.success(
        `Generated codes for ${payload.length} compan${payload.length === 1 ? 'y' : 'ies'}`,
      );
    } catch {
      toast.error('Failed to generate some codes');
    }
  };

  const handleBulkDelete = async (selected: any[]) => {
    if (selected.length === 0) return;
    try {
      await bulkDeleteMutation.mutateAsync(selected.map((item) => item.id));
      toast.success(
        `Deleted ${selected.length} compan${selected.length === 1 ? 'y' : 'ies'}`,
      );
    } catch {
      toast.error('Failed to delete some companies');
    }
  };

  return (
    <PharmacyLookupPage
      title="Pharmaceutical Companies"
      description="Manage medicine manufacturing and supplying companies"
      icon={Building2}
      items={items}
      isLoading={isLoading}
      error={error}
      search={search}
      setSearch={setSearch}
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      editingItem={editingItem}
      setEditingItem={setEditingItem}
      formData={formData}
      setFormData={setFormData}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      isSubmitting={createMutation.isPending || updateMutation.isPending}
      isDeleting={deleteMutation.isPending}
      codeGenerator={generateCompanyCode}
      onBulkGenerateCodes={handleBulkGenerateCodes}
      isBulkGenerating={bulkGenerateMutation.isPending}
      onBulkDelete={handleBulkDelete}
      isBulkDeleting={bulkDeleteMutation.isPending}
      extraFields={[
        { name: 'code', label: 'Code', placeholder: 'e.g. SPL' },
        { name: 'contact', label: 'Contact', placeholder: 'Phone / email / address' },
        { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Optional description' },
      ]}
      page={page}
      limit={limit}
      total={meta?.total || 0}
      totalPages={meta?.totalPages || 1}
      onPageChange={setPage}
      itemName="companies"
    />
  );
}
